import './ViewAttributesDropdown.scss';
import { faChevronDown, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { ClickAwayListener } from '@mui/base';
import { Button, Checkbox, Divider, Dropdown, Menu, MenuButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useParams } from 'react-router';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';

export interface IAttribute {
  id: string;
  text: string;
}

interface IViewAttributesDropdownProps {
  selectedAttributes: string[];
  attributes: Record<string, { text: string; id: string; projectId: string }[]>;
  showAttributesNames: boolean;
  selectedAttrutesChanged: (attributes: string[]) => void;
  showAttributesNamesChanged: (show: boolean) => void;
}

export const ViewAttributesDropdown = observer((props: IViewAttributesDropdownProps): ReactElement => {
  const { attributes, selectedAttributes, showAttributesNames, selectedAttrutesChanged, showAttributesNamesChanged } = props;
  const { t } = useTranslator();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const allSelected = selectedAttributes.length === Object.entries(attributes).flatMap(([_, attrs]) => attrs).length;
  const { projectId } = useParams<{ projectId: string }>();

  const handleShowAll = (): void => {
    selectedAttrutesChanged(allSelected ? [] : Object.entries(attributes).flatMap(([_, attrs]) => attrs.map((x) => x.id)));
  };

  const handleClick = (selected: IAttribute): void => {
    const output = selectedAttributes.slice();
    const element = output.find((o) => o === selected.id);
    const index = element ? output.indexOf(element) : -1;
    if (index < 0) {
      output.push(selected.id);
    } else {
      output.splice(index, 1);
    }
    selectedAttrutesChanged(output);
  };

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          setIsOpen(false);
        }}
      >
        <div className='view-attributes-dropdown'>
          <Dropdown open={isOpen}>
            <MenuButton
              onClick={() => {
                setIsOpen((x) => !x);
              }}
              disabled={!Object.entries(attributes).flatMap(([_, attrs]) => attrs).length}
              slots={{ root: Button }}
              slotProps={{ root: { variant: 'topbar', color: 'neutral', className: 'view-attributes-dropdown-button' } }}
              startDecorator={<FontAwesomeIcon icon={selectedAttributes.length ? faEye : faEyeSlash} />}
              endDecorator={<FontAwesomeIcon icon={faChevronDown} />}
            >
              {t('attributes')}
            </MenuButton>
            <Menu
              placement='bottom-start'
              className='view-attributes-dropdown-menu'
            >
              <div className='view-attributes-dropdown-menu-content'>
                <div className='view-attributes-dropdown-menu-section'>
                  <Checkbox
                    label='Show Attributes Names'
                    checked={showAttributesNames}
                    onClick={(e) => {
                      e.stopPropagation();
                      showAttributesNamesChanged(!showAttributesNames);
                    }}
                  />
                </div>
                <Divider>{t('pick_attributes')}</Divider>
                <div className='view-attributes-dropdown-menu-section'>
                  <Checkbox
                    checked={allSelected}
                    label={allSelected ? t('deselect_all') : t('select_all')}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowAll();
                    }}
                  />
                </div>
                {Object.entries(attributes)
                  .filter(([_, attrs]) => attrs.length > 0)
                  .map(([key, attrs]) => (
                    <div key={key}>
                      {attrs[0].projectId !== projectId && (
                        <Divider>
                          <div
                            title={key}
                            className='view-attributes-dropdown-menu-section-title'
                          >
                            {key}
                          </div>
                        </Divider>
                      )}
                      <RowLayout
                        size='md'
                        className={'checkbox-multi-select view-attributes-multiselect'}
                      >
                        {attrs.map((o) => (
                          <S_Checkbox
                            id={`${o.id}-custom-input`}
                            className='checkbox-multiselect-item'
                            key={o.id}
                            checked={attrs.filter((x) => selectedAttributes.includes(x.id)).some((x) => x.id === o.id)}
                            label={<>{o.text}</>}
                            disabled={false}
                            readOnly={true}
                            onClick={(): void => {
                              handleClick(o);
                            }}
                          />
                        ))}
                      </RowLayout>
                    </div>
                  ))}
              </div>
            </Menu>
          </Dropdown>
        </div>
      </ClickAwayListener>
    </>
  );
});
